import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import { useBgReport, useBgJob } from './BackgroundJobs';

// Verifica Lucchetto SINGOLA (staff) per Codice Fiscale/P.IVA via /staff-ai/lucchetto:
// accoda sul pool SalesMate di sistema. Estetica fumé + background/FAB condiviso.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const TERMIN = ['completato', 'errore', 'annullato'];
const vLabel = (v) => ({ VERDE: 'Vendibile (verde)', GRIGIO: 'Presidiato (grigio)', 'NON TROVATO': 'Non trovato' }[v] || v || '—');
const vColor = (v) => v === 'VERDE' ? '#22c55e' : v === 'GRIGIO' ? '#9ca3af' : '#64748b';

export default function LucchettoDialog({ open, onClose }) {
  const [cf, setCf] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);
  const clear = () => { clearInterval(poll.current); setCf(''); setBusy(false); setJob(null); setJobId(null); };

  const avvia = async () => {
    const c = cf.trim();
    if (!c) return;
    clearInterval(poll.current); setBusy(true); setJob(null);
    try {
      const r = await axios.post(`${API}/staff-ai/lucchetto`, { codice_fiscale: c });
      const id = r.data.id; setJobId(id);
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/staff-ai/lucchetto/${id}`);
          setJob(s.data);
          if (TERMIN.includes(s.data.stato)) { clearInterval(poll.current); setBusy(false); }
        } catch { /* ritenta */ }
      }, 1500);
    } catch (e) { setBusy(false); setJob({ stato: 'errore', errore: e.response?.data?.detail || 'Errore avvio' }); }
  };

  const ris = job?.risultato;
  const errTxt = job?.errore || (ris && !ris.success && (ris.site_down ? 'SalesMate non raggiungibile' : ris.quota_exceeded ? 'Quota SalesMate esaurita' : 'Verifica non riuscita'));
  const verdetto = ris && ris.success ? ris.lucchetto : null;
  const errore = job?.stato === 'errore' || (job?.stato === 'completato' && ris && !ris.success);
  const done = !!job && TERMIN.includes(job.stato);
  const summary = errore ? (errTxt || 'non riuscito') : verdetto ? vLabel(verdetto) : null;

  useBgReport('lucchetto', { label: 'Lucchetto', color: '#eab308', iconKey: 'lucchetto' },
    { open, running: busy, done: done && !errore, error: errore, summary, jobId, active: busy || !!job });

  const entry = useBgJob('lucchetto');
  const had = useRef(false);
  useEffect(() => { if (entry) had.current = true; else if (had.current) { had.current = false; clear(); } }, [entry]); // eslint-disable-line
  useEffect(() => {
    if (open && !busy && !job && entry && entry.jobId) {
      axios.get(`${API}/staff-ai/lucchetto/${entry.jobId}`).then((r) => { if (TERMIN.includes(r.data.stato)) setJob(r.data); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Verifica Lucchetto</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {busy && (
                <button onClick={onClose} title="Metti in background: la verifica continua, riapri dal pulsante in basso a destra"
                  style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>⤓ Background</button>
              )}
              <button className="bulk-x" onClick={onClose}><IconClose /></button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input value={cf} onChange={(e) => setCf(e.target.value)} placeholder="Codice Fiscale / P.IVA"
              onKeyDown={(e) => { if (e.key === 'Enter') avvia(); }} disabled={busy}
              style={{ flex: 1, minWidth: 0, padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 13, textTransform: 'uppercase' }} />
            <button onClick={avvia} disabled={busy || !cf.trim()}
              style={{ padding: '9px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy || !cf.trim() ? 'default' : 'pointer', opacity: busy || !cf.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#facc15,#ca8a04)' }}>
              {busy ? '…' : 'Verifica'}
            </button>
          </div>

          {busy ? (
            <div style={{ padding: '4px 0 6px' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>
                {job?.stato === 'in_coda' ? `In coda${job.posizione ? ` · posizione ${job.posizione}` : ''}…` : 'Verifica sul pool SalesMate…'}
              </div>
              <div className="indaga-track"><div className="indaga-fill" style={{ width: '100%', background: 'linear-gradient(90deg,#ca8a04,#facc15,#ca8a04)' }} /></div>
            </div>
          ) : job ? (
            errTxt ? <div style={{ color: '#fca5a5', fontSize: 13 }}>{errTxt}</div> : (
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: '#0b1a10', background: vColor(verdetto), padding: '6px 14px', borderRadius: 10, marginBottom: 8 }}>
                  {vLabel(verdetto)}
                </div>
                {ris?.mapped?.ragione_sociale && (
                  <div style={{ fontSize: 13, color: '#fff', marginTop: 4 }}>{ris.mapped.ragione_sociale}</div>
                )}
                {ris?.user_label && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 6 }}>account: {ris.user_label}</div>}
              </div>
            )
          ) : entry && entry.status !== 'running' ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '8px 0' }}>Ultimo esito: <b style={{ color: '#fff' }}>{entry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova verifica sopra)</span></div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '8px 0' }}>
              Inserisci Codice Fiscale/P.IVA: la verifica del lucchetto entra in coda sul pool SalesMate di sistema e ti dice se è vendibile (verde) o presidiato (grigio).
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
